const DEFAULT_WORKER_ENDPOINT = '/services/youtube-feed';
const CTA_LABEL = 'View our library on YouTube';

const MESSAGES = {
  loading: 'Loading videos...',
  missingFeedUrl: 'Add a YouTube channel or playlist URL to load videos.',
  missingEndpoint: 'Add a Worker endpoint to load videos.',
  invalidEndpoint: 'The Worker endpoint is not a valid URL or path.',
  endpointNotFound: 'The Worker endpoint could not be found.',
  empty: 'No videos were returned from this feed.',
  invalidResponse: 'The Worker response did not include a videos array.',
  error: 'Videos could not be loaded right now.',
};

/**
 * @typedef {Object} YouTubeFeedCarouselSettings
 * @property {'channel' | 'playlist'} sourceType
 * @property {string} feedUrl
 * @property {string} workerEndpoint
 * @property {number} maxVideos
 * @property {boolean} autoplay
 * @property {number} autoplaySpeed
 * @property {boolean} loop
 * @property {string} iconsStyle
 * @property {string} iconsShape
 * @property {string} paginationStyle
 * @property {boolean} showCtaSlide
 * @property {boolean} showTitle
 * @property {boolean} useGutters
 */

/**
 * @typedef {Object} NormalizedVideo
 * @property {string} id
 * @property {string} title
 * @property {string} url
 * @property {string} thumbnailUrl
 * @property {string} publishedAt
 */

/**
 * @typedef {{ error: true, message: string } | { error: false, url: URL }} FeedEndpointResult
 */

class YouTubeFeedCarousel extends HTMLElement {
  constructor() {
    super();

    /** @type {AbortController} */
    this.abortController = new AbortController();
  }

  connectedCallback() {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.load();
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  async load() {
    this.clearSlideshow();

    const settings = this.settings;

    if (!settings.feedUrl) {
      this.setState('error', MESSAGES.missingFeedUrl);
      return;
    }

    const endpoint = this.getEndpointUrl(settings);

    if (endpoint.error) {
      this.setState('endpoint', endpoint.message);
      return;
    }

    this.setState('loading', MESSAGES.loading);

    try {
      const response = await fetch(endpoint.url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        credentials: endpoint.url.origin === window.location.origin ? 'same-origin' : 'omit',
        signal: this.abortController.signal,
      });

      if (response.status === 404) {
        this.setState('endpoint', MESSAGES.endpointNotFound);
        return;
      }

      if (!response.ok) {
        throw new Error(`Worker returned ${response.status}`);
      }

      /** @type {{ videos?: unknown[] }} */
      const data = await response.json();

      if (!Array.isArray(data?.videos)) {
        this.setState('error', MESSAGES.invalidResponse);
        return;
      }

      /** @type {NormalizedVideo[]} */
      const videos = [];

      for (const videoData of data.videos) {
        const video = this.normalizeVideo(videoData);
        if (!video) continue;

        videos.push(video);
        if (videos.length >= settings.maxVideos) break;
      }

      if (!videos.length) {
        this.setState('empty', MESSAGES.empty);
        return;
      }

      this.renderVideos(videos, settings);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      console.error('YouTube feed carousel failed to load:', error);
      this.setState('error', MESSAGES.error);
    }
  }

  /**
   * @returns {YouTubeFeedCarouselSettings}
   */
  get settings() {
    /** @type {'channel' | 'playlist'} */
    const sourceType = this.dataset.sourceType === 'playlist' ? 'playlist' : 'channel';
    const maxVideos = this.clampNumber(this.dataset.maxVideos, 6, 1, 12);
    const workerEndpoint =
      this.dataset.workerEndpoint == null ? DEFAULT_WORKER_ENDPOINT : this.cleanString(this.dataset.workerEndpoint);

    return {
      sourceType,
      feedUrl: this.cleanString(this.dataset.feedUrl),
      workerEndpoint,
      maxVideos,
      autoplay: this.dataset.autoplay === 'true',
      autoplaySpeed: this.clampNumber(this.dataset.autoplaySpeed, 5, 3, 7),
      loop: this.dataset.loop !== 'false',
      iconsStyle: this.cleanString(this.dataset.iconsStyle) || 'arrow',
      iconsShape: this.cleanString(this.dataset.iconsShape) || 'none',
      paginationStyle: this.cleanString(this.dataset.paginationStyle) || 'dots',
      showCtaSlide: this.dataset.showCtaSlide === 'true',
      showTitle: this.dataset.showTitle !== 'false',
      useGutters: this.dataset.useGutters === 'true',
    };
  }

  /**
   * @param {YouTubeFeedCarouselSettings} settings
   * @returns {FeedEndpointResult}
   */
  getEndpointUrl(settings) {
    if (!settings.workerEndpoint) {
      return {
        error: true,
        message: MESSAGES.missingEndpoint,
      };
    }

    try {
      if (!settings.workerEndpoint.startsWith('/') && !/^https?:\/\//i.test(settings.workerEndpoint)) {
        return {
          error: true,
          message: MESSAGES.invalidEndpoint,
        };
      }

      const url = new URL(settings.workerEndpoint, window.location.origin);

      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return {
          error: true,
          message: MESSAGES.invalidEndpoint,
        };
      }

      url.searchParams.set('source_type', settings.sourceType);
      url.searchParams.set('url', settings.feedUrl);
      url.searchParams.set('limit', String(settings.maxVideos));

      return {
        error: false,
        url,
      };
    } catch (_error) {
      return {
        error: true,
        message: MESSAGES.invalidEndpoint,
      };
    }
  }

  /**
   * @param {unknown} value
   * @returns {NormalizedVideo | null}
   */
  normalizeVideo(value) {
    if (!this.isRecord(value)) return null;

    const video = value;
    const id = this.cleanString(video.id ?? video.videoId ?? video.video_id);
    const title = this.cleanString(video.title ?? video.name) || 'YouTube video';
    const url =
      this.safeHttpUrl(video.url ?? video.watchUrl ?? video.watch_url ?? video.link) ||
      (id ? `https://www.youtube.com/watch?v=${encodeURIComponent(id)}` : '');

    if (!url) return null;

    return {
      id: id || url,
      title,
      url,
      thumbnailUrl: this.getThumbnailUrl(video),
      publishedAt: this.cleanString(video.publishedAt ?? video.published_at),
    };
  }

  /**
   * @param {Record<string, unknown>} video
   * @returns {string}
   */
  getThumbnailUrl(video) {
    const directThumbnail =
      video.thumbnail ?? video.thumbnailUrl ?? video.thumbnail_url ?? video.image ?? video.imageUrl ?? video.image_url;

    const directUrl = this.thumbnailCandidateToUrl(directThumbnail);
    if (directUrl) return directUrl;

    const thumbnails = video.thumbnails;
    if (Array.isArray(thumbnails)) {
      return this.getBestThumbnailUrl(thumbnails);
    }

    if (this.isRecord(thumbnails)) {
      return this.getBestThumbnailUrl(Object.values(thumbnails));
    }

    return '';
  }

  /**
   * @param {unknown[]} candidates
   * @returns {string}
   */
  getBestThumbnailUrl(candidates) {
    return (
      candidates
        .map((candidate) => ({
          url: this.thumbnailCandidateToUrl(candidate),
          width: this.getThumbnailWidth(candidate),
        }))
        .filter((candidate) => candidate.url)
        .sort((a, b) => b.width - a.width)[0]?.url || ''
    );
  }

  /**
   * @param {unknown} candidate
   * @returns {string}
   */
  thumbnailCandidateToUrl(candidate) {
    if (typeof candidate === 'string') return this.safeHttpUrl(candidate);
    if (this.isRecord(candidate)) return this.safeHttpUrl(candidate.url);

    return '';
  }

  /**
   * @param {unknown} candidate
   * @returns {number}
   */
  getThumbnailWidth(candidate) {
    if (!this.isRecord(candidate)) return 0;

    const width = Number(candidate.width);

    return Number.isFinite(width) ? width : 0;
  }

  /**
   * @param {NormalizedVideo[]} videos
   * @param {YouTubeFeedCarouselSettings} settings
   * @returns {void}
   */
  renderVideos(videos, settings) {
    const slideContents = videos.map((video) => this.createVideoCard(video, settings));
    const ctaCard = settings.showCtaSlide ? this.createCtaCard(settings.feedUrl) : null;

    if (ctaCard) slideContents.push(ctaCard);

    const slideCount = slideContents.length;
    const slideshow = document.createElement('slideshow-component');
    slideshow.className = 'resource-list__carousel youtube-feed-carousel__slideshow';
    slideshow.dataset.youtubeFeedSlideshow = '';
    slideshow.style.setProperty('--slideshow-timeline', this.createTimeline(slideCount));
    slideshow.style.setProperty('--slideshow-align-items', 'stretch');
    slideshow.style.setProperty('--slide-min-height', 'auto');
    slideshow.style.setProperty('--slide-min-height-desktop', 'auto');
    slideshow.setAttribute('initial-slide', '0');

    if (settings.autoplay && slideCount > 1) {
      slideshow.setAttribute('autoplay', String(settings.autoplaySpeed));
      slideshow.setAttribute('aria-live', 'polite');
    }

    if (settings.loop && slideCount > 1) {
      slideshow.setAttribute('infinite', '');
    }

    const container = document.createElement('slideshow-container');
    container.setAttribute('ref', 'slideshowContainer');

    if (settings.iconsStyle !== 'none' && slideCount > 1) {
      container.append(this.createArrows(settings));
    }

    const slides = document.createElement('slideshow-slides');
    slides.setAttribute('tabindex', '-1');
    slides.setAttribute('ref', 'scroller');

    if (settings.useGutters) {
      slides.setAttribute('gutters', 'start end');
    }

    slideContents.forEach((content, index) => {
      const slideId = `youtube-feed-${index}-${this.sanitizeId(videos[index]?.id ?? 'cta')}`;
      slides.append(this.createSlide(content, index, slideId));
    });

    container.append(slides);
    slideshow.append(container);

    const controls = this.createControls(slideCount, settings);
    if (controls) slideshow.append(controls);

    this.clearSlideshow();
    this.append(slideshow);
    this.setState('ready', '');
  }

  /**
   * @param {NormalizedVideo} video
   * @param {YouTubeFeedCarouselSettings} settings
   * @returns {HTMLElement}
   */
  createVideoCard(video, settings) {
    const card = document.createElement('div');
    card.className = 'resource-card youtube-feed-card';
    card.dataset.resourceType = 'video';

    card.append(this.createCardLink(video.url, `Watch ${video.title} on YouTube`));

    const media = document.createElement('div');
    media.className = 'resource-card__media';

    if (video.thumbnailUrl) {
      const image = document.createElement('img');
      image.className = 'resource-card__image youtube-feed-card__image';
      image.src = video.thumbnailUrl;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.sizes = '(min-width: 750px) 33vw, 70vw';
      media.append(image);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'resource-card__image-placeholder';
      placeholder.textContent = video.title;
      media.append(placeholder);
    }

    const play = document.createElement('span');
    play.className = 'youtube-feed-card__play';
    play.setAttribute('aria-hidden', 'true');
    play.append(this.cloneIcon('play'));
    media.append(play);
    card.append(media);

    if (settings.showTitle) {
      const content = document.createElement('div');
      content.className = 'resource-card__content';

      const title = document.createElement('p');
      title.className = 'resource-card__title paragraph';
      title.textContent = video.title;
      content.append(title);
      card.append(content);
    }

    return card;
  }

  /**
   * @param {string} feedUrl
   * @returns {HTMLElement | null}
   */
  createCtaCard(feedUrl) {
    const url = this.safeHttpUrl(feedUrl);
    if (!url) return null;

    const card = document.createElement('div');
    card.className = 'resource-card youtube-feed-card youtube-feed-card--cta';
    card.dataset.resourceType = 'video';

    card.append(this.createCardLink(url, CTA_LABEL));

    const media = document.createElement('div');
    media.className = 'resource-card__media';

    const label = document.createElement('p');
    label.className = 'youtube-feed-card__cta-label';
    label.textContent = CTA_LABEL;

    media.append(label);
    card.append(media);

    return card;
  }

  /**
   * @param {string} href
   * @param {string} label
   * @returns {HTMLAnchorElement}
   */
  createCardLink(href, label) {
    const link = document.createElement('a');
    link.className = 'resource-card__link';
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    const hiddenLabel = document.createElement('span');
    hiddenLabel.className = 'visually-hidden';
    hiddenLabel.textContent = label;
    link.append(hiddenLabel);

    return link;
  }

  /**
   * @param {HTMLElement} content
   * @param {number} index
   * @param {string} slideId
   * @returns {HTMLElement}
   */
  createSlide(content, index, slideId) {
    const slide = document.createElement('slideshow-slide');
    slide.className = 'resource-list__slide';
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    slide.setAttribute('slide-id', slideId);
    slide.style.setProperty('--slideshow-timeline', `--slide-${index + 1}`);
    slide.style.setProperty('--product-media-fit', 'cover');
    slide.style.setProperty('--grid-template-rows', 'auto 1fr');
    slide.style.setProperty('--grid-template-rows-desktop', 'auto 1fr');
    slide.append(content);

    return slide;
  }

  /**
   * @param {YouTubeFeedCarouselSettings} settings
   * @returns {HTMLElement}
   */
  createArrows(settings) {
    const arrows = document.createElement('slideshow-arrows');
    arrows.setAttribute('position', 'center');
    arrows.append(this.createArrow('previous', settings), this.createArrow('next', settings));

    return arrows;
  }

  /**
   * @param {'previous' | 'next'} action
   * @param {YouTubeFeedCarouselSettings} settings
   * @returns {HTMLButtonElement}
   */
  createArrow(action, settings) {
    const iconName = settings.iconsStyle.includes('chevron') ? 'caret' : 'arrow';
    const button = document.createElement('button');
    const classes = [
      'slideshow-control',
      `slideshow-control--${action}`,
      `slideshow-control--style-${settings.iconsStyle}`,
      'button',
      'button-unstyled',
      'button-unstyled--transparent',
    ];

    if (settings.iconsStyle.includes('large')) classes.splice(2, 0, 'slideshow-control--large');
    if (settings.iconsShape !== 'none') classes.splice(2, 0, `slideshow-control--shape-${settings.iconsShape}`);
    if (action === 'previous') classes.push('flip-x');

    button.type = 'button';
    button.className = classes.join(' ');
    button.setAttribute('aria-label', action === 'previous' ? 'Previous slide' : 'Next slide');
    button.setAttribute('on:click', `/${action}`);
    button.setAttribute('ref', action);

    const wrapper = document.createElement('span');
    wrapper.className = `svg-wrapper icon-${iconName}`;
    wrapper.append(this.cloneIcon(iconName));

    button.append(wrapper);

    return button;
  }

  /**
   * @param {number} slideCount
   * @param {YouTubeFeedCarouselSettings} settings
   * @returns {HTMLElement | null}
   */
  createControls(slideCount, settings) {
    if (settings.paginationStyle === 'none' || slideCount <= 1) return null;

    const controls = document.createElement('slideshow-controls');
    controls.setAttribute('ref', 'slideshowControls');
    controls.setAttribute('pagination-position', 'center');
    controls.setAttribute('scroll-mode', 'horizontal');

    if (settings.autoplay) {
      controls.append(this.createAutoplayButton('pause'), this.createAutoplayButton('play'));
    }

    if (settings.paginationStyle === 'counter') {
      const counter = document.createElement('div');
      counter.className = 'slideshow-controls__counter';

      const current = document.createElement('span');
      current.setAttribute('ref', 'current');
      current.textContent = '1';

      const slash = document.createElement('span');
      slash.className = 'slash';
      slash.textContent = '/';

      counter.append(current, slash, document.createTextNode(String(slideCount)));
      controls.append(counter);

      return controls;
    }

    const dots = document.createElement('ol');
    dots.className = 'slideshow-controls__dots';

    for (let index = 0; index < slideCount; index++) {
      const item = document.createElement('li');
      const dot = document.createElement('button');
      dot.className = 'slideshow-control button button-unstyled';
      dot.type = 'button';
      dot.style.setProperty('animation-timeline', `--slide-${index + 1}`);
      dot.setAttribute('aria-label', `Slide ${index + 1} of ${slideCount}`);
      dot.setAttribute('on:click', `/select/${index}`);
      dot.setAttribute('ref', 'dots[]');
      dot.textContent = String(index + 1);

      if (index === 0) {
        dot.setAttribute('aria-selected', 'true');
      }

      item.append(dot);
      dots.append(item);
    }

    controls.append(dots);

    return controls;
  }

  /**
   * @param {'pause' | 'play'} action
   * @returns {HTMLButtonElement}
   */
  createAutoplayButton(action) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `slideshow-control button-secondary button-unstyled icon-${action}`;
    button.setAttribute('aria-label', action === 'pause' ? 'Pause slideshow' : 'Play slideshow');
    button.setAttribute('on:click', `/${action}`);
    button.append(this.cloneIcon(action));

    return button;
  }

  /**
   * @param {string} name
   * @returns {Node}
   */
  cloneIcon(name) {
    const template = this.querySelector(`template[data-youtube-feed-icon="${name}"]`);
    if (!(template instanceof HTMLTemplateElement)) return document.createDocumentFragment();

    return template.content.cloneNode(true);
  }

  /**
   * @param {'loading' | 'ready' | 'empty' | 'error' | 'endpoint'} state
   * @param {string} message
   * @returns {void}
   */
  setState(state, message) {
    const status = this.querySelector('[data-youtube-feed-status]');
    if (!(status instanceof HTMLElement)) return;

    if (state === 'ready') {
      status.hidden = true;
      return;
    }

    status.hidden = false;
    status.dataset.state = state;
    status.setAttribute('role', state === 'error' || state === 'endpoint' ? 'alert' : 'status');

    const title = status.querySelector('.youtube-feed-carousel__status-title');
    if (title) title.textContent = message;
  }

  clearSlideshow() {
    for (const slideshow of this.querySelectorAll('[data-youtube-feed-slideshow]')) {
      slideshow.remove();
    }
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  createTimeline(count) {
    return Array.from({ length: count }, (_item, index) => `--slide-${index + 1}`).join(',');
  }

  /**
   * @param {unknown} value
   * @param {number} fallback
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  clampNumber(value, fallback, min, max) {
    const number = Number.parseInt(String(value), 10);
    if (Number.isNaN(number)) return fallback;

    return Math.min(Math.max(number, min), max);
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  cleanString(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  safeHttpUrl(value) {
    const candidate = this.cleanString(value);
    if (!candidate) return '';

    try {
      const url = new URL(candidate);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';

      return url.href;
    } catch (_error) {
      return '';
    }
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  sanitizeId(value) {
    return this.cleanString(value).replace(/[^a-zA-Z0-9_-]/g, '') || 'slide';
  }

  /**
   * @param {unknown} value
   * @returns {value is Record<string, unknown>}
   */
  isRecord(value) {
    return value !== null && typeof value === 'object';
  }
}

if (!customElements.get('youtube-feed-carousel')) {
  customElements.define('youtube-feed-carousel', YouTubeFeedCarousel);
}
